import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback } from "react";
import LogLayout from "../components/LogLayout";
import {
  FolderOpen, FileText, Plus, Search, ChevronRight, ArrowLeft, Building2,
  CreditCard, Receipt, FileCheck, Send, Trash2, TrendingUp, BarChart3, Package
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from "recharts";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;
const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0));

const DOC_TYPE_ICONS = {
  facture: FileText, remise_cheque: CreditCard, bordereau: Receipt,
  bon_livraison: Package, attestation: FileCheck, courrier: Send, autre: FileText,
};
const DOC_TYPE_COLORS = {
  facture: "#3b82f6", remise_cheque: "#10b981", bordereau: "#f59e0b",
  bon_livraison: "#8b5cf6", attestation: "#06b6d4", courrier: "#ec4899", autre: "#64748b",
};
const CHART_COLORS = ["#0ea5e9", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4", "#ef4444", "#64748b"];

export default function LogAdminFilesPage() {
  const [pharmacies, setPharmacies] = useState([]);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [pharmacyData, setPharmacyData] = useState(null);
  const [medications, setMedications] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegister, setShowRegister] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("documents");
  const [form, setForm] = useState({
    document_type: "facture", title: "", référence: "", amount: "",
    insurer_name: "", notes: "", date_document: "",
  });

  const loadPharmacies = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/log/admin-files/pharmacies`, { headers: getHeaders() });
      setPharmacies(res.data.pharmacies || []);
    } catch { }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadPharmacies(); }, [loadPharmacies]);

  const selectPharmacy = async (phId) => {
    setSelectedPharmacy(phId);
    setLoading(true);
    try {
      const [filesRes, medsRes] = await Promise.all([
        axios.get(`${API}/log/admin-files/pharmacy/${phId}`, { headers: getHeaders() }),
        axios.get(`${API}/log/admin-files/top-medications?pharmacy_id=${phId}`, { headers: getHeaders() }),
      ]);
      setPharmacyData(filesRes.data);
      setMedications(medsRes.data.medications || []);
      setCategories(medsRes.data.categories || []);
    } catch { }
    finally { setLoading(false); }
  };

  const registerDocument = async () => {
    if (!form.title) { toast.error("Titre requis"); return; }
    try {
      await axios.post(`${API}/log/admin-files/register`, {
        pharmacy_id: selectedPharmacy, ...form,
        amount: form.amount ? parseFloat(form.amount) : 0,
      }, { headers: getHeaders() });
      toast.success("Document enregistré ! Pharmacie notifiee.");
      setShowRegister(false);
      setForm({ document_type: "facture", title: "", référence: "", amount: "", insurer_name: "", notes: "", date_document: "" });
      selectPharmacy(selectedPharmacy);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    try {
      await axios.delete(`${API}/log/admin-files/${docId}`, { headers: getHeaders() });
      toast.success("Document supprimé");
      selectPharmacy(selectedPharmacy);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const filteredPharmacies = pharmacies.filter(p =>
    (p.pharmacy_name || p.full_name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.email || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && !selectedPharmacy) return <LogLayout title="Dossiers Pharmacies"><div className="flex justify-center py-20"><FolderOpen className="w-6 h-6 text-sky-400 animate-spin" /></div></LogLayout>;

  // Pharmacy list view
  if (!selectedPharmacy) return (
    <LogLayout title="Dossiers Pharmacies" subtitle="Enregistrez des documents pour les pharmacies clients">
      <div data-testid="admin-files-page" className="space-y-4">
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Rechercher une pharmacie..."
              className="bg-slate-900/60 border-white/10 text-white pl-10" data-testid="search-pharmacy" />
          </div>
          <span className="text-xs text-slate-500">{filteredPharmacies.length} pharmacie{filteredPharmacies.length !== 1 ? "s" : ""}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="pharmacies-grid">
          {filteredPharmacies.map(ph => (
            <button key={ph.id} onClick={() => selectPharmacy(ph.id)} data-testid={`pharmacy-${ph.id}`}
              className="bg-slate-900/60 border border-white/5 rounded-xl p-4 text-left hover:border-sky-500/30 transition-all group">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-sky-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-white truncate">{ph.pharmacy_name || ph.full_name}</p>
                  <p className="text-[10px] text-slate-500 truncate">{ph.email} {ph.commune ? `— ${ph.commune}` : ""}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-600 group-hover:text-sky-400 transition-colors" />
              </div>
              <div className="flex gap-3 mt-3">
                <div className="bg-slate-800/60 rounded-lg px-2 py-1"><p className="text-[10px] text-slate-500">Documents</p><p className="text-sm font-bold text-white">{ph.doc_count}</p></div>
                <div className="bg-slate-800/60 rounded-lg px-2 py-1"><p className="text-[10px] text-slate-500">Factures</p><p className="text-sm font-bold text-white">{ph.invoice_count}</p></div>
              </div>
            </button>
          ))}
        </div>
      </div>
    </LogLayout>
  );

  // Pharmacy detail view
  const ph = pharmacyData?.pharmacy;
  const docs = pharmacyData?.documents || [];
  const invoices = pharmacyData?.invoices || [];

  return (
    <LogLayout title={ph?.pharmacy_name || ph?.full_name || "Dossier"} subtitle="Dossier pharmacie">
      <div data-testid="pharmacy-detail" className="space-y-4">
        {/* Back + info */}
        <div className="flex items-center gap-3">
          <Button onClick={() => { setSelectedPharmacy(null); setPharmacyData(null); }} variant="outline" size="sm"
            className="border-white/10 text-slate-400 text-xs" data-testid="back-btn">
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Retour
          </Button>
          <div className="flex-1">
            <p className="text-sm font-bold text-white">{ph?.pharmacy_name || ph?.full_name}</p>
            <p className="text-[10px] text-slate-500">{ph?.email} {ph?.commune ? `— ${ph.commune}` : ""} {ph?.phone ? `— ${ph.phone}` : ""}</p>
          </div>
          <Button onClick={() => setShowRegister(!showRegister)} className="bg-emerald-500 hover:bg-emerald-400 text-white text-xs" data-testid="register-doc-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Enregistrer un document
          </Button>
        </div>

        {/* Register form */}
        {showRegister && (
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-5 space-y-3" data-testid="register-form">
            <h3 className="text-sm font-bold text-emerald-400">Enregistrer un document</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] text-slate-500 uppercase mb-1 block">Type de document</label>
                <select value={form.document_type} onChange={e => setForm(p => ({ ...p, document_type: e.target.value }))}
                  className="bg-slate-900/60 border border-white/10 rounded-lg text-white text-sm h-9 px-3 w-full" data-testid="doc-type">
                  <option value="facture">Facture</option>
                  <option value="remise_cheque">Remise de chèque</option>
                  <option value="bordereau">Bordereau</option>
                  <option value="bon_livraison">Bon de livraison</option>
                  <option value="attestation">Attestation</option>
                  <option value="courrier">Courrier</option>
                  <option value="autre">Autre</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase mb-1 block">Titre</label>
                <Input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  placeholder="Ex: Facture NSIA Janvier 2026" className="bg-slate-900/60 border-white/10 text-white" data-testid="doc-title" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase mb-1 block">Référence</label>
                <Input value={form.référence} onChange={e => setForm(p => ({ ...p, référence: e.target.value }))}
                  placeholder="Ex: FAC-2026-001" className="bg-slate-900/60 border-white/10 text-white" data-testid="doc-ref" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase mb-1 block">Montant (FCFA)</label>
                <Input type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))}
                  placeholder="0" className="bg-slate-900/60 border-white/10 text-white" data-testid="doc-amount" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase mb-1 block">Assureur</label>
                <Input value={form.insurer_name} onChange={e => setForm(p => ({ ...p, insurer_name: e.target.value }))}
                  placeholder="Ex: NSIA" className="bg-slate-900/60 border-white/10 text-white" data-testid="doc-insurer" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 uppercase mb-1 block">Date document</label>
                <Input type="date" value={form.date_document} onChange={e => setForm(p => ({ ...p, date_document: e.target.value }))}
                  className="bg-slate-900/60 border-white/10 text-white" data-testid="doc-date" />
              </div>
              <div className="md:col-span-3">
                <label className="text-[10px] text-slate-500 uppercase mb-1 block">Notes</label>
                <textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Informations complementaires..." rows={2}
                  className="w-full bg-slate-900/60 border border-white/10 rounded-lg text-white text-sm px-3 py-2 resize-none" data-testid="doc-notes" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={registerDocument} className="bg-emerald-500 hover:bg-emerald-400 text-white text-sm" data-testid="submit-doc">
                <Send className="w-4 h-4 mr-1.5" /> Enregistrer et notifier
              </Button>
              <Button onClick={() => setShowRegister(false)} variant="outline" className="border-white/10 text-slate-400 text-sm">Annuler</Button>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-900/60 border border-white/5 rounded-xl p-1">
          {[
            { v: "documents", l: "Documents CMES", icon: FolderOpen, count: docs.length },
            { v: "invoices", l: "Factures", icon: FileText, count: invoices.length },
            { v: "medications", l: "Medicaments vendus", icon: TrendingUp, count: medications.length },
          ].map(t => (
            <button key={t.v} onClick={() => setActiveTab(t.v)} data-testid={`tab-${t.v}`}
              className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${activeTab === t.v ? "bg-sky-500 text-white" : "text-slate-400 hover:text-white"}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.l} <span className="text-[9px] opacity-70">({t.count})</span>
            </button>
          ))}
        </div>

        {/* Documents Tab */}
        {activeTab === "documents" && (
          <div className="space-y-2" data-testid="documents-list">
            {docs.length === 0 ? (
              <div className="text-center py-12"><FolderOpen className="w-10 h-10 text-slate-200 mx-auto mb-3" /><p className="text-sm text-slate-500">Aucun document enregistré.</p></div>
            ) : docs.map(doc => {
              const DocIcon = DOC_TYPE_ICONS[doc.document_type] || FileText;
              const color = DOC_TYPE_COLORS[doc.document_type] || "#64748b";
              return (
                <div key={doc.id} className="bg-slate-900/60 border border-white/5 rounded-xl p-4 flex items-center gap-3" data-testid={`doc-${doc.id}`}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: color + "20" }}>
                    <DocIcon className="w-5 h-5" style={{ color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white truncate">{doc.title}</p>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-[9px] px-2 py-0.5 rounded-full font-bold" style={{ background: color + "20", color }}>{doc.document_type_label}</span>
                      {doc.référence && <span className="text-[10px] text-slate-500">Ref: {doc.référence}</span>}
                      {doc.amount > 0 && <span className="text-[10px] font-bold text-white">{fmt(doc.amount)} FCFA</span>}
                      {doc.insurer_name && <span className="text-[10px] text-sky-400">{doc.insurer_name}</span>}
                    </div>
                    <p className="text-[10px] text-slate-500 mt-0.5">Par {doc.registered_by_name} — {new Date(doc.created_at).toLocaleDateString("fr-FR")}</p>
                  </div>
                  <Button size="sm" variant="outline" className="border-red-500/20 text-red-400 text-xs hover:bg-red-500/100/100/10"
                    onClick={() => deleteDoc(doc.id)} data-testid={`del-${doc.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === "invoices" && (
          <div className="space-y-2" data-testid="invoices-list">
            {invoices.length === 0 ? (
              <div className="text-center py-12"><FileText className="w-10 h-10 text-slate-200 mx-auto mb-3" /><p className="text-sm text-slate-500">Aucune facture.</p></div>
            ) : invoices.slice(0, 30).map(inv => (
              <div key={inv.id} className="bg-slate-900/60 border border-white/5 rounded-xl p-3 flex items-center gap-3" data-testid={`inv-${inv.id}`}>
                <FileText className="w-5 h-5 text-sky-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-white truncate">{inv.invoice_number || inv.id}</p>
                  <p className="text-[10px] text-slate-500">{inv.insurer_name} — {fmt(inv.amount)} FCFA — {inv.status}</p>
                </div>
                <span className="text-[10px] text-slate-500">{inv.created_at ? new Date(inv.created_at).toLocaleDateString("fr-FR") : ""}</span>
              </div>
            ))}
          </div>
        )}

        {/* Medications Tab - Chart + Table */}
        {activeTab === "medications" && (
          <div className="space-y-4" data-testid="medications-analysis">
            {medications.length === 0 ? (
              <div className="text-center py-12"><Package className="w-10 h-10 text-slate-200 mx-auto mb-3" /><p className="text-sm text-slate-500">Aucune donnee de medicaments.</p></div>
            ) : (
              <>
                {/* Charts row */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Bar chart - Top medications by quantity */}
                  <div className="bg-slate-900/60 border border-white/5 rounded-xl p-4">
                    <h4 className="text-xs font-bold text-white mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-sky-400" /> Top medicaments (quantite)</h4>
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={medications.slice(0, 10)} layout="vertical">
                        <XAxis type="number" tick={{ fill: "#64748b", fontSize: 10 }} />
                        <YAxis type="category" dataKey="name" width={120} tick={{ fill: "#94a3b8", fontSize: 10 }} />
                        <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "8px", fontSize: 12 }}
                          formatter={(v) => [fmt(v), "Quantite"]} labelStyle={{ color: "#fff" }} />
                        <Bar dataKey="total_quantity" radius={[0, 4, 4, 0]}>
                          {medications.slice(0, 10).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  {/* Pie chart - Categories */}
                  <div className="bg-slate-900/60 border border-white/5 rounded-xl p-4">
                    <h4 className="text-xs font-bold text-white mb-3 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-400" /> Répartition par categorie</h4>
                    {categories.length > 0 ? (
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie data={categories} dataKey="total_value" nameKey="category" cx="50%" cy="50%" outerRadius={90}
                            label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}>
                            {categories.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                          </Pie>
                          <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: "8px", fontSize: 12 }}
                            formatter={(v) => [`${fmt(v)} FCFA`, "Valeur"]} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : <p className="text-xs text-slate-500 text-center py-10">Pas de données</p>}
                  </div>
                </div>

                {/* Table */}
                <div className="bg-slate-900/60 border border-white/5 rounded-xl overflow-hidden">
                  <div className="p-4 border-b border-white/5">
                    <h4 className="text-xs font-bold text-white flex items-center gap-2"><Package className="w-4 h-4 text-amber-400" /> Tableau detaille des medicaments</h4>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs" data-testid="medications-table">
                      <thead>
                        <tr className="border-b border-white/5">
                          <th className="text-left p-3 text-slate-500 font-medium">#</th>
                          <th className="text-left p-3 text-slate-500 font-medium">Medicament</th>
                          <th className="text-left p-3 text-slate-500 font-medium">Categorie</th>
                          <th className="text-right p-3 text-slate-500 font-medium">Quantite</th>
                          <th className="text-right p-3 text-slate-500 font-medium">Prix moy.</th>
                          <th className="text-right p-3 text-slate-500 font-medium">Valeur totale</th>
                        </tr>
                      </thead>
                      <tbody>
                        {medications.map((med, i) => (
                          <tr key={i} className="border-b border-white/5 hover:bg-white/5">
                            <td className="p-3 text-slate-500">{i + 1}</td>
                            <td className="p-3 text-white font-medium">{med.name}</td>
                            <td className="p-3"><span className="px-2 py-0.5 rounded-full text-[10px] bg-sky-500/10 text-sky-400 border border-sky-500/20">{med.category || "—"}</span></td>
                            <td className="p-3 text-right text-white font-bold">{fmt(med.total_quantity)}</td>
                            <td className="p-3 text-right text-slate-400">{fmt(med.avg_price)} FCFA</td>
                            <td className="p-3 text-right text-emerald-400 font-bold">{fmt(med.total_value)} FCFA</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </LogLayout>
  );
}
